import { useTranslation } from 'react-i18next';

import { App } from 'antd';
import qs from 'qs';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiErrorEnum } from '../enums/ApiErrorEnum';
import { CookieItemKeyEnum } from '../enums/CookieItemKeyEnum';
import { EventBusEventEnum } from '../enums/EventBusEventEnum';
import { FileLimitationEnum } from '../enums/FileLimitationEnum';
import { FilesTableEnum } from '../enums/FilesTableEnum';
import { FileTypeEnum } from '../enums/FileTypeEnum';
import { StorageItemKeyEnum } from '../enums/StorageItemKeyEnum';
import type File from '../interfaces/File';
import type FilePermissions from '../interfaces/FilePermissions';
import type InfoViewer from '../interfaces/InfoViewer';
import EventBus from '../services/EventBus';
import { FileService } from '../services/fileService';
import { dateFromTicks, dateToTicks, dateUtcToLocal, detectFileType } from '../services/utils';
import { setCookie } from '../utils/cookie';
import { translate } from '../utils/translation';
import { copyToClipboard, hasFileLimitation } from '../utils/utils';
import { useAuth } from './useAuth';
import { useFileDownload } from './useFileDownload';
import { useModal } from './useModal';
import { usePermissions } from './usePermissions';
import { useTags } from './useTags';

const fileService = new FileService();

interface RedirectFileToLocationProps {
  file: File;
  avoidOpenViewer?: boolean;
  queryParams?: Record<string, string>;
}

function useOpenFile() {
  const { t } = useTranslation();
  const { authenticatedUser, isOnBehalfOfUser, isExternal, deviceInfo, isInFileRoute } = useAuth();
  const { getCountFilesUnread } = usePermissions();
  const { handleDownloadFiles } = useFileDownload();
  const { notification } = App.useApp();
  const { tags } = useTags();
  const navigate = useNavigate();
  const modal = useModal();

  /**
   * A function to safely open a file with specific checks for user authentication,
   * file tagging, and enforced modes.
   *
   * This function utilizes a number of checks to ensure that files are not opened
   * when certain conditions are not met, such as lack of user authentication or if
   * the action is being performed on behalf of another user.
   *
   * File tags are evaluated against a list of forced anti-capture mode tags to determine
   * if a special mode should be activated when opening the file.
   *
   * @type {Function}
   * @param {File} file - The file object to be opened. Must contain `fileId`, `uid`,
   * and optionally `tags`.
   * @returns {void}
   *
   * File opening consideration steps:
   * 1. Verify the presence of an authenticated user and file identifiers.
   * 2. Preclude action if operating on behalf of another user.
   * 3. Map file's tags to known tag titles for consideration.
   * 4. Assess whether any file tag triggers forced anti-capture mode.
   * 5. Open the file in the appropriate mode based on the presence of enforced tags.
   */
  const openFileSafely: (file: File, onBlankPage?: true) => boolean = useCallback(
    (file: File, onBlankPage: boolean = true) => {
      if (!authenticatedUser?.id || !file.cufi || !file.id) {
        return false;
      }

      if (isOnBehalfOfUser) {
        alert(translate('cannot_open_file_on_behalf'));
        return false;
      }

      // Check if the file size is greater than 250mb
      if (file.fileSize > 262144000 && detectFileType(file.fileExtension) !== FileTypeEnum.Video) {
        modal
          .open({
            title: t('modal.fileSizeTooBigForOpen.title'),
            message: t('modal.fileSizeTooBigForOpen.description', {
              value: '250MB',
            }),
          })
          .then(() => {
            handleDownloadFiles([file.id], file.fileName);
          })
          .catch(() => {
            return;
          });

        return false;
      }

      // CHeck if is in the shared folder
      if (isInFileRoute(FilesTableEnum.Shared)) {
        setTimeout(() => {
          getCountFilesUnread().then(() => { });
        }, 2500);
      }

      if (hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileScreenCapture)) {
        window.open(`/file/protected/${file?.id}`, '_blank')?.focus();
        return true;
      }

      EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
        isVisible: true,
      });
      // Get file and check permission to redirect give errors or redirect straight to /file/{fileId} route
      fileService
        .getFilePermissions(file.id)
        .then((response) => {
          checkFileRedirect(response, file, authenticatedUser?.id, onBlankPage);
        })
        .catch((error) => {
          switch (error.response) {
            case ApiErrorEnum.AntiCaptureActivated:
              notification.error({
                message: t('errors.antiCaptureActivated'),
              });

              window.open(`/file/protected/${file.id}`, '_blank')?.focus();
              break;
            case ApiErrorEnum.DownloadRestrictionActivated:
              notification.error({
                message: t('errors.downloadRestrictionActivated'),
              });
              window.open(`/file/protected/${file.id}`, '_blank')?.focus();
              break;
            default:
              // Check if file is has shortcut as extension
              if (file.isShortcut) {
                notification.error({
                  message: t('errors.openShortcutFileError'),
                });
              } else {
                notification.error({
                  message: t('errors.requestFilePermissions'),
                });
              }
          }
        })
        .finally(() => {
          EventBus.emit(EventBusEventEnum.IsGlobalLoaderVisible, {
            isVisible: false,
          });
        });

      return true;
    },
    [tags, authenticatedUser?.id, isOnBehalfOfUser, isExternal],
  );

  /**
   * Redirects the user based on the properties of the provided file.
   *
   * @param fileInfo
   * @param {Object} file - The file object used for determining the redirection.
   * @param userId
   * @param onBlankPage
   * @param {Object} file.fileProtected - The protected properties of the file.
   * @param {string} file.fileProtected.fileExtension - The extension of the file.
   * @param {string} file.fileProtected.userId - The ID of the user who owns the file.
   * @param {string} file.fileProtected.fileId - The ID of the file.
   */
  const checkFileRedirect = (
    fileInfo: FilePermissions,
    file: File,
    userId: string | number | undefined | null,
    onBlankPage: boolean = true,
  ) => {
    if (fileInfo.fileProtected.fileExtension === '') {
      if (userId && userId.toString() === fileInfo?.fileProtected?.userId?.toString()) {
        navigate(`/files/${FilesTableEnum.Personal}/folder/${fileInfo.fileProtected.fileId}`);
      } else {
        navigate(`/files/${FilesTableEnum.Received}/folder/${fileInfo.fileProtected.fileId}`);
      }
      return;
    } else {
      openFileViewer(fileInfo, file, onBlankPage).then(() => { });
    }
  };

  /**
   * Get the right table for a file
   * @param file
   * @param currentUserId
   * @returns
   */
  const getTableForFile = (file: File, currentUserId: number) => {
    if (file.isDeleted) return FilesTableEnum.Trash;
    if (file.isArchived) return FilesTableEnum.Hidden;
    if (file.isFavourite) return FilesTableEnum.Favorites;
    if (file.isShared) return FilesTableEnum.Shared;
    return file.ownerId === currentUserId ? FilesTableEnum.Personal : FilesTableEnum.Received;
  };

  /**
   * Handles redirecting a file or folder to the appropriate location based on its state and properties.
   *
   * @param {File} file - The file or folder object to be redirected.
   * @fires EventBusEventEnum.OnFilesFiltersChange - When the file or folder meets specific criteria, an event is emitted to update the file filters.
   *
   * This handler performs the following actions:
   * - Blurs the global search field if it is a folder.
   * - Redirects to the appropriate location based on the folder status such as:
   *   - Regular folder in files
   *   - Received folder
   *   - Shared folder
   *   - Favourite folder
   *   - Archived folder
   *   - Deleted folder
   * - Opens a new tab for non-folder files.
   *
   * The redirect and event emission are determined based on the current URL and folder properties such as `isFavourite`, `isArchived`, `isDeleted`,
   * `isShared`, `ownerId`, and `authenticatedUser.userId`.
   */

  const redirectFileToLocation = ({
    file,
    avoidOpenViewer = false,
    queryParams = {},
  }: RedirectFileToLocationProps) => {
    const me = authenticatedUser?.id!;
    let targetPath: string;

    if (file.isFolder || avoidOpenViewer) {
      const table = getTableForFile(file, me);
      const folderId = file.isFolder ? file.id : file.parentId;
      targetPath = folderId ? `/files/${table}/folder/${folderId}` : `/files/${table}`;
    } else if (file.isDeleted) {
      targetPath = `/files/${FilesTableEnum.Trash}`;
    } else {
      // file “normale”
      return openFileSafely(file);
    }

    const stringifiesParams = qs.stringify(queryParams, { skipNulls: true });

    if (window.location.pathname === targetPath) {
      notification.info({
        message: t('notifications.fileLocatedInThisLocation'),
        showProgress: true,
        pauseOnHover: true,
      });
    } else {
      navigate(`${targetPath}?${stringifiesParams}`, { replace: true });
    }
  };

  /**
   * Copy the file link to the clipboard
   * @param file
   * @returns
   */
  const copyFileLinkToClipboard = (file: File) => {
    const link = `${window.location.origin}/file/${file.id}`;
    copyToClipboard(link);
  };

  /**
   * Constructs an InfoViewer object based on the provided requested file details.
   *
   * @param {FilePermissions} requestedFile - The file metadata and permissions information needed to create the InfoViewer.
   * @param file
   * @returns {InfoViewer} An object containing detailed information about the requested file and its access limitations.
   */
  const getInfoViewer = (requestedFile: FilePermissions, file: File): InfoViewer => {
    const infoViewer: InfoViewer = {};
    if (requestedFile.fileGranted.fileLimitation !== null) {
      infoViewer.limitations = requestedFile.fileGranted.fileLimitation;
      if (
        infoViewer?.limitations?.accessExpiration &&
        infoViewer?.limitations?.accessExpiration > 0
      ) {
        // ricalcola il ticks sulla base del fuso orario locale
        infoViewer.limitations.accessExpiration = dateToTicks(
          dateUtcToLocal(dateFromTicks(infoViewer.limitations.accessExpiration)),
        );
      }
      infoViewer.limitations.canDownload = !hasFileLimitation(
        file.fileLimitations,
        FileLimitationEnum.DenyFileDownload,
      );
    } else {
      infoViewer.limitations = {};
      infoViewer.limitations.accessExpiration = 0;
      infoViewer.limitations.canEdit = true;
      infoViewer.limitations.canDownload = !hasFileLimitation(
        file.fileLimitations,
        FileLimitationEnum.DenyFileDownload,
      );

      infoViewer.limitations.canOpenWithOffice = true;
    }
    const fileExt = requestedFile.fileProtected.fileExtension;
    infoViewer.fileType = detectFileType(fileExt);
    infoViewer.isOffice = infoViewer?.fileType
      ? [
        FileTypeEnum.Word,
        FileTypeEnum.Excel,
        FileTypeEnum.Powerpoint,
        FileTypeEnum.Wopitest,
      ].includes(infoViewer.fileType as FileTypeEnum)
      : false;
    infoViewer.fileId = requestedFile.fileProtected.fileId;
    infoViewer.fileUid = requestedFile.fileProtected.uid;
    infoViewer.userId = authenticatedUser?.id;
    infoViewer.userName = authenticatedUser?.userName;
    infoViewer.originaleFileName = requestedFile.fileProtected.originaleFileName;
    infoViewer.fileName = requestedFile.fileProtected.originaleFileName;
    infoViewer.fileSize = requestedFile.fileProtected.fileSize;
    infoViewer.fileExtension = requestedFile.fileProtected.fileExtension;
    infoViewer.fileCreation = requestedFile.fileProtected.createdAt;

    if (requestedFile.fileProtected.isFileForSale && requestedFile.fileForSale !== null) {
      infoViewer.isPackage = true;
      infoViewer.limitations.DenyEditing = true;

      infoViewer.fileName = requestedFile.fileForSale.title;
      infoViewer.fileDescription = requestedFile.fileForSale.description;
      infoViewer.fileImage = requestedFile.fileForSale.thumbnail;
      infoViewer.canBeResold = requestedFile.fileForSale.isResalable;
      infoViewer.isUnsealed = requestedFile.fileForSale.isUnsealed;

      const date = dateFromTicks(requestedFile.fileProtected.createdAt);
      infoViewer.urlNFF = `${import.meta.env.VITE_APP_AZURE_STORAGE_URL}/${import.meta.env.VITE_APP_AZURE_FILES_CONTAINER}/Sales Files v2/${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}/${requestedFile.fileProtected.fileId}.nff`;
    }

    return infoViewer;
  };

  /**
   * Opens the given file in the appropriate viewer or office application.
   *
   * @param {FilePermissions} requestedFile - The file information needed to open the file.
   *
   * This function determines the type of the file based on its extension and opens it using the appropriate viewer.
   * If the file is not for sale and has permission to edit, it verifies whether it can be opened in an office application.
   * For supported document types (Word, Excel, PowerPoint), a new window is opened for viewing or editing the document.
   * Otherwise, it tries to navigate to a respective viewer page based on the file type.
   * The function also sets session storage items to indicate the file is being opened online.
   *
   * @param file
   * @param onBlankPage
   * @return {Promise<void>} - Returns a promise that resolves to true when the file has been successfully opened.
   */
  const openFileViewer = async (
    requestedFile: FilePermissions,
    file: File,
    onBlankPage: boolean = true,
  ): Promise<void> => {
    const fileExt = requestedFile.fileProtected.fileExtension;
    let fileType = detectFileType(fileExt);
    const fileOpened = getInfoViewer(requestedFile, file);

    try {
      sessionStorage.setItem(
        StorageItemKeyEnum.fileOnline,
        fileOpened.fileExtension === '.svg' ? String(false) : String(true),
      ); // false se vettoriale

      localStorage.removeItem(StorageItemKeyEnum.FileOpened);
      localStorage.setItem(StorageItemKeyEnum.FileOpened, JSON.stringify(fileOpened));
      //await storeStorageItem('sessionStorage', StorageItemKeyEnum.FileOpened, fileOpened);
    } catch (error) {
      console.error('Error saving to sessionStorage:', error);
    }

    // Verify the ability to open in office (only for documents not for sale and with permission to edit)
    let canOpenOffice = false;
    if (!requestedFile.fileProtected.isFileForSale) {
      canOpenOffice = true;
      if (requestedFile.fileGranted.fileLimitation !== null)
        canOpenOffice = requestedFile.fileGranted.fileLimitation.canEdit || false;
    }

    if (fileType === FileTypeEnum.Wopitest) {
      if (onBlankPage) {
        window.open(`/wopitest/${file.id}`, '_blank')?.focus();
      } else {
        navigate(`/wopitest/${file.id}`);
      }
      return;
    }

    if (
      canOpenOffice &&
      [
        FileTypeEnum.Word,
        FileTypeEnum.Excel,
        FileTypeEnum.Powerpoint,
        FileTypeEnum.Wopitest,
      ].includes(fileType)
    ) {
      {
        setCookie(CookieItemKeyEnum.officeFile, requestedFile.fileProtected.originaleFileName, {
          path: '/',
        });

        if (onBlankPage) {
          window.open(`/viewer/pdf?lng=${deviceInfo?.culture}`, '_blank')?.focus();
        } else {
          navigate(`/viewer/pdf?lng=${deviceInfo?.culture}`);
        }
      }
    } else {
      if ([FileTypeEnum.Word, FileTypeEnum.Excel, FileTypeEnum.Powerpoint].includes(fileType)) {
        fileType = FileTypeEnum.Pdf;
      }

      if ([FileTypeEnum.Code].includes(fileType)) {
        fileType = FileTypeEnum.Unknown;
      }
      if (onBlankPage) {
        window.open(`/viewer/${fileType.toLowerCase()}?lng=${deviceInfo?.culture}`, '_blank')?.focus();
      } else {
        navigate(`/viewer/${fileType.toLowerCase()}?lng=${deviceInfo?.culture}`);
      }
    }
  };

  return {
    openFileSafely,
    redirectFileToLocation,
    getInfoViewer,
    openFileViewer,
    getTableForFile,
    copyFileLinkToClipboard,
  };
}

export default useOpenFile;
